import React, { useEffect, useRef, ReactElement } from 'react';
import { debounce } from 'lodash';
import Helmet from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Toolbar,
  Typography,
} from '@material-ui/core';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

import ButtonBadge from 'retired/ButtonBadge';
import { TableBox } from 'retired/TableBox';
import TablePaginationActions from 'retired/TablePaginationActions';
import { selectOrganization, selectUser } from 'entities/user/selectors';
import { selectDisplayedVolunteers, selectTotal } from 'entities/volunteers/selectors';
import Api from 'entities/api';
import config from 'config';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { Privileges } from 'utils/privileges';
import { PageBannerContainer } from 'components/PageBannerContainer/PageBannerContainer';
import VolunteerPageBanner from 'assets/pageBanners/volunteers.avif';
import { useVolunteerTimesWithPaginationQuery, VolunteerTimeApproval } from 'generated/graphql';
import { Features } from 'utils/features';

import VolunteersTable from './VolunteersTable';
import Filters from './Filters';
import * as Actions from './actions';
import reducer from './reducers';
import epics from './epics';
import { selectLoading, selectVerifying, selectFetchParams } from './selectors';
import { UploadPacModal } from './UploadPacModal';
import { sendPacUploadEmailAction } from './actions';

const key = 'volunteersPage';

const VolunteersListPage = (): ReactElement => {
  const history = useHistory();

  const volunteers = useSelector(selectDisplayedVolunteers);
  const loading = useSelector(selectLoading);
  const total = useSelector(selectTotal);
  const organization = useSelector(selectOrganization);
  const user = useSelector(selectUser);
  const verifying = useSelector(selectVerifying);
  const fetchParams = useSelector(selectFetchParams);

  const volunteerTimeQuery = {
    approval: { equals: VolunteerTimeApproval.Pending },
  };

  if (organization?.isCompany()) {
    volunteerTimeQuery.companyId = { equals: organization.id };
  } else {
    volunteerTimeQuery.nonprofitId = { equals: organization.id };
  }

  const volunteerTimeQueryVariables = {
    where: volunteerTimeQuery,
    take: 1,
  };

  const { data } = useVolunteerTimesWithPaginationQuery({
    variables: volunteerTimeQueryVariables,
  });

  const volunteerTimesCount = data?.volunteerTimesWithPagination?.metadata?.totalItems;

  const dispatch = useDispatch();
  const searchInputRef = useRef();

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    if (organization) {
      dispatch(Actions.initAction());
    }
  }, [dispatch, organization]);

  const isCompany = organization?.isCompany();
  const isPacUploadEnabled =
    user?.hasFeature(Features.PAC_UPLOAD) &&
    user?.hasPrivilege(Privileges.DONATION_MATCH_PAC_UPLOAD);

  const goToPage = (__: unknown, pageNum: number): void => {
    dispatch(Actions.setFetchParamsAction({ pageNum }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      Actions.setFetchParamsAction({
        limit: Number(event.target.value),
      })
    );
  };

  const sendPacUploadEmailNotification = (message: string) => {
    dispatch(sendPacUploadEmailAction(message));
  };

  // Since React synthetic events are reused we should extract
  // the event value outside of the debounced function
  const handleSearch = (search: string): void => {
    dispatch(Actions.setFetchParamsAction({ search }));
  };

  const handleSearchDebounced = debounce((search) => handleSearch(search), 600);

  const sortTableBy = (column: string, reserve = false): void => {
    const defaultOrder = reserve ? 'asc' : 'desc';
    const reverseOrder = reserve ? 'desc' : 'asc';
    dispatch(
      Actions.setFetchParamsAction({
        sort: column,
        order:
          fetchParams.get('sort') !== column || fetchParams.get('order') === reverseOrder
            ? defaultOrder
            : reverseOrder,
      })
    );
  };

  const renderTableHead = (): ReactElement => (
    <TableHead>
      <TableRow>
        <TableCell size="medium">
          <TableSortLabel
            direction={fetchParams.get('order')}
            active={fetchParams.get('sort') === 'name'}
            onClick={(): void => sortTableBy('name')}
          >
            Name
          </TableSortLabel>
        </TableCell>
        {isCompany && (
          <TableCell size="medium">
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'department'}
              onClick={(): void => sortTableBy('department')}
            >
              Department
            </TableSortLabel>
          </TableCell>
        )}
        <TableCell size="medium">
          {/* eslint-disable-next-line no-constant-condition */}
          {false /* Disabled until optimized for large datasets */ ? (
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'city'}
              onClick={(): void => sortTableBy('city')}
            >
              City
            </TableSortLabel>
          ) : (
            'City'
          )}
        </TableCell>
        <TableCell size="medium">
          {/* eslint-disable-next-line no-constant-condition */}
          {false /* Disabled until optimized for large datasets */ ? (
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'state'}
              onClick={(): void => sortTableBy('state')}
            >
              State
            </TableSortLabel>
          ) : (
            'State'
          )}
        </TableCell>
        <TableCell size="medium">
          {/* eslint-disable-next-line no-constant-condition */}
          {false /* Disabled until optimized for large datasets */ ? (
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'country'}
              onClick={(): void => sortTableBy('country')}
            >
              Country
            </TableSortLabel>
          ) : (
            'Country'
          )}
        </TableCell>
        <TableCell size="medium">
          {isCompany ? (
            <TableSortLabel
              direction={fetchParams.get('order')}
              active={fetchParams.get('sort') === 'hours'}
              onClick={(): void => sortTableBy('hours', true)}
            >
              Hours
            </TableSortLabel>
          ) : (
            'Hours'
          )}
        </TableCell>
        <TableCell size="medium">
          <TableSortLabel
            direction={fetchParams.get('order')}
            active={fetchParams.get('sort') === 'phone'}
            onClick={(): void => sortTableBy('phone')}
          >
            Phone
          </TableSortLabel>
        </TableCell>
        <TableCell size="medium">
          <TableSortLabel
            direction={fetchParams.get('order')}
            active={fetchParams.get('sort') === 'email'}
            onClick={(): void => sortTableBy('email')}
          >
            Email
          </TableSortLabel>
        </TableCell>
        {isCompany ? (
          <>
            {user?.hasPrivilege(Privileges.DONATION_VIEW) && (
              <TableCell size="medium">Matching Cap Balance</TableCell>
            )}
            <TableCell size="medium" style={{ width: 136 }}>
              Approve / Deny
            </TableCell>
          </>
        ) : null}
      </TableRow>
    </TableHead>
  );

  if (user && !user.hasPrivilege(Privileges.ORGANIZATION_MANAGE_USERS)) {
    history.replace('/');
  }

  const [openPacUploadModal, setOpenPacUploadModal] = React.useState(false);
  const onClosePacUploadModal = () => setOpenPacUploadModal(false);

  return (
    <>
      <Helmet title={isCompany ? 'Employees' : 'Volunteers'} />

      <PageBannerContainer color="#E5E0F5" backgroundImage={VolunteerPageBanner}>
        <h1>{isCompany ? 'Employees' : 'Volunteers'}</h1>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1em',
            justifyContent: 'flex-end',
          }}
        >
          {isPacUploadEnabled && (
            <ButtonBadge badgeContent={null} color="primary">
              <Button size="large" onClick={() => setOpenPacUploadModal(true)}>
                Upload PAC
              </Button>
            </ButtonBadge>
          )}

          {user?.isAdmin && (
            <Link to="/volunteers/new">
              <ButtonBadge badgeContent={null} color="primary">
                <Button size="large">Add Employee</Button>
              </ButtonBadge>
            </Link>
          )}

          {user?.hasPrivilege(Privileges.VOLUNTEERTIME) && (
            <Link to="/volunteer-times">
              <ButtonBadge badgeContent={volunteerTimesCount || null} color="primary">
                <Button size="large">Approve Volunteer Hours</Button>
              </ButtonBadge>
            </Link>
          )}

          {user?.hasFeature(Features.VOLUNTEER_REWARD) &&
            user?.hasPrivilege(Privileges.VOLUNTEER_REWARDS_MANAGE) && (
              <Link to="/volunteer-rewards">
                <ButtonBadge badgeContent={null} color="primary">
                  <Button size="large">Approve Volunteer Grants</Button>
                </ButtonBadge>
              </Link>
            )}
        </div>
      </PageBannerContainer>

      <Filters />

      <TableBox
        loading={loading}
        fixedTop={
          <Toolbar style={{ marginTop: -4 }}>
            <Typography
              variant="h6"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textTransform: 'capitalize',
              }}
            >
              {fetchParams.get('status') === 'accepted'
                ? 'Approved'
                : fetchParams.get('status') || 'All'}{' '}
              {isCompany ? 'Employees' : 'Volunteers'}
            </Typography>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginLeft: 'auto',
              }}
            >
              <TextField
                type="search"
                size="small"
                placeholder="Search"
                inputRef={searchInputRef}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear"
                        onClick={(): void => {
                          if (searchInputRef.current) {
                            searchInputRef.current.value = '';
                          }
                          handleSearch('');
                        }}
                        size="small"
                        disabled={!fetchParams.get('search')}
                      >
                        <ClearIcon fontSize="inherit" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                  handleSearchDebounced(event.target.value)
                }
                style={{
                  width: 350,
                }}
              />
            </div>
          </Toolbar>
        }
        fixedBottom={
          <>
            {/* eslint-disable jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events */}
            {organization && total < 50000 ? (
              <a
                style={{ marginLeft: 5, outline: 'none' }}
                onClick={(e: React.MouseEvent<HTMLElement>): void => {
                  const a = e.target.tagName === 'A' ? e.target : e.target.parentElement;
                  if (!a.href) {
                    const query = {
                      sort: fetchParams.get('sort'),
                      order: fetchParams.get('order'),
                      search: fetchParams.get('search'),
                    };
                    const queryStr = Object.keys(query)
                      .filter((k: string) => query[k])
                      .map((k: string) => `${k}=${query[k]}`)
                      .join('&');
                    fetch(
                      `${config.apiUrl}/api/organizations/${organization?.id}/${
                        isCompany ? 'employees' : 'volunteers'
                      }/export?${queryStr}`,
                      {
                        method: 'GET',
                        headers: {
                          Authorization: Api.defaultHeaders.Authorization,
                        },
                      }
                    )
                      .then((response) => response.blob())
                      .then((blob) => {
                        const url = window.URL.createObjectURL(blob);
                        a.href = url;
                        a.download = `${isCompany ? 'employees' : 'volunteers'}.csv`;
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.removeAttribute('href');
                      });
                  }
                }}
                role="button"
                tabIndex={0}
                title="Download CSV file"
              >
                Export
              </a>
            ) : (
              <div />
            )}

            <TablePagination
              component="div"
              count={total || 0}
              rowsPerPage={fetchParams.get('limit')}
              page={fetchParams.get('pageNum')}
              onChangePage={goToPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </>
        }
      >
        <VolunteersTable
          volunteers={volunteers}
          header={renderTableHead()}
          isCompany={isCompany}
          verifying={verifying}
          user={user}
          loading={loading}
        />
      </TableBox>
      <UploadPacModal
        isModalOpen={openPacUploadModal}
        onClose={onClosePacUploadModal}
        sendPacUploadEmailNotification={sendPacUploadEmailNotification}
      />
    </>
  );
};

export default VolunteersListPage;
